/**
 * 通知权限状态机 + 用户手势守卫。
 *
 * 两个已知失败：
 *
 * 1. **权限申请必须在用户手势内发起**。`await 拿个配置` 之后再调 `requestPermission()`，
 *    在 iOS 上就废了——而且是**静默**废：不抛错，直接返回 'default'，用户看到"点了没反应"。
 *    这里用一个手势追踪器把它变成一个明确的失败原因（requires-user-gesture），
 *    调用方至少知道自己写错了。
 *
 * 2. **拒绝是不可逆的**。浏览器只允许问一次；被拒之后再调什么都不会发生。所以正确的顺序是
 *    先用自己的界面把"为什么需要通知"说清楚（primer），用户同意了再调系统弹窗。
 *    这个 primer 的展示状态也在这里持久化。
 */
import { explainNotificationSupport, readNotificationPermission } from './capability.js';
import { fail, failFrom, ok } from './result.js';
import { createSafeStorage } from './storage.js';
const GESTURE_EVENTS = ['pointerdown', 'pointerup', 'mousedown', 'keydown', 'touchend', 'click'];
/**
 * 追踪最近一次用户输入。
 *
 * 浏览器里没有"当前是否在用户手势中"的可读 API（`navigator.userActivation` 有，但覆盖面
 * 还不够，且 isActive 的窗口比我们想卡的严）。用时间近似：默认 1 秒内有过真实输入就认。
 * 这是个**守卫**不是**保证**——它的价值在于把一个静默失败变成一条明确的错误。
 */
export function createGestureTracker(target = typeof window === 'undefined' ? undefined : window) {
    let last = Number.NEGATIVE_INFINITY;
    const now = () => (typeof performance === 'undefined' ? Date.now() : performance.now());
    const mark = () => {
        last = now();
    };
    // 捕获阶段：即使业务代码 stopPropagation 也拦不住
    const opts = { capture: true, passive: true };
    if (target)
        for (const e of GESTURE_EVENTS)
            target.addEventListener(e, mark, opts);
    return {
        fresh(maxAgeMs = 1000) {
            // navigator.userActivation 存在时优先信它（更准），不存在再用时间近似
            const ua = typeof navigator !== 'undefined' ? navigator.userActivation : undefined;
            if (ua && typeof ua.isActive === 'boolean' && ua.isActive)
                return true;
            return now() - last <= maxAgeMs;
        },
        lastAt: () => last,
        dispose() {
            if (target)
                for (const e of GESTURE_EVENTS)
                    target.removeEventListener(e, mark, opts);
        },
    };
}
export function createNotificationPermission(ns, options = {}) {
    const storage = options.storage ?? createSafeStorage();
    const gesture = options.gesture ?? createGestureTracker();
    const PRIMER = ns.key('notify.primer');
    const listeners = new Set();
    let lastSeen = readState();
    function readState() {
        const p = readNotificationPermission();
        return p === 'unsupported' ? 'unsupported' : p;
    }
    function emit() {
        const next = readState();
        if (next === lastSeen)
            return;
        lastSeen = next;
        for (const l of listeners)
            l(next);
    }
    // 用户可能在系统设置里改权限，页面收不到任何事件；回前台时对一次。
    const onVisible = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible')
            emit();
    };
    // 回前台重读权限：用户可能刚去系统设置里改过，而浏览器不会为此发任何事件。
    //
    // ⚠️ 挂/摘必须**跟着订阅者数量走**，而且要能重新挂上。
    // 以前是"构造时挂一次、最后一个订阅者退订时摘掉、之后再也不挂"——
    // React StrictMode 一上来就是 subscribe → unsubscribe → subscribe，
    // 于是开发环境里这个能力**一启动就永久没了**，而它不会报任何错。
    let attached = false;
    const attach = () => {
        if (attached || typeof document === 'undefined')
            return;
        document.addEventListener('visibilitychange', onVisible);
        attached = true;
    };
    const detach = () => {
        if (!attached || typeof document === 'undefined')
            return;
        document.removeEventListener('visibilitychange', onVisible);
        attached = false;
    };
    return {
        state: readState,
        primerShown: () => storage.get(PRIMER) === '1',
        markPrimerShown: () => {
            storage.set(PRIMER, '1');
        },
        async request({ requireGesture = true, gestureMaxAgeMs = 1000 } = {}) {
            const support = explainNotificationSupport();
            if (!support.ok)
                return support;
            const current = readState();
            if (current === 'granted')
                return ok(current);
            // 已拒绝：浏览器不会再弹，别让调用方以为"再问一次"有用
            if (current === 'denied')
                return fail('permission-denied');
            if (requireGesture && !gesture.fresh(gestureMaxAgeMs)) {
                return fail('requires-user-gesture', '权限申请必须在用户点击的同步路径里发起；await 之后再调在 iOS 上会静默失败');
            }
            try {
                // 老 Safari 只有回调版；新版返回 Promise。两种都兜住。
                const result = await new Promise((resolve, reject) => {
                    try {
                        const maybe = Notification.requestPermission((p) => resolve(p));
                        if (maybe && typeof maybe.then === 'function') {
                            ;
                            maybe.then(resolve, reject);
                        }
                    }
                    catch (e) {
                        reject(e);
                    }
                });
                emit();
                if (result === 'granted')
                    return ok('granted');
                if (result === 'denied')
                    return fail('permission-denied');
                // 'default' = 用户把弹窗划掉了，没做选择。还能再问，不算拒绝。
                return fail('permission-default');
            }
            catch (e) {
                return failFrom('failed', e);
            }
        },
        subscribe(listener) {
            listeners.add(listener);
            attach();
            return () => {
                listeners.delete(listener);
                if (listeners.size === 0)
                    detach();
            };
        },
    };
}
//# sourceMappingURL=permission.js.map